import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Container, Grid, Card, CardContent, 
    Paper, useTheme, Chip, Button, Table, TableBody, 
    TableCell, TableHead, TableRow, IconButton, Tooltip, 
    TextField, Alert, Divider, 
    LinearProgress // <---- FIX: LinearProgress component imported
} from '@mui/material';
import { 
    Api as ApiIcon, 
    Send as SendIcon, 
    CheckCircleOutline as SuccessIcon,
    ErrorOutline as ErrorIcon,
    AccessTime as PendingIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';

// --- MOCK DATA ---

const initialWebhooks = [
    { name: 'Customer Service CRM', url: 'https://crm.example.com/api/v1/event', event: 'user.created', status: 'Active' },
    { name: 'External Analytics Tool', url: 'https://stats.external.io/data/ingest', event: 'order.completed', status: 'Active' },
    { name: 'Legacy Billing System', url: 'https://billing.old.net/hook', event: 'invoice.paid', status: 'Inactive' },
];

const mockLogs = [
    { id: 'wh-005', webhook: 'Customer Service CRM', event: 'user.created', payloadSize: '2.5 KB', responseCode: 200, status: 'Success', timestamp: '2025-10-06 12:00:00' },
    { id: 'wh-004', webhook: 'External Analytics Tool', event: 'order.completed', payloadSize: '4.1 KB', responseCode: 500, status: 'Failed', timestamp: '2025-10-06 11:58:30' },
    { id: 'wh-003', webhook: 'Customer Service CRM', event: 'user.created', payloadSize: '2.4 KB', responseCode: 200, status: 'Success', timestamp: '2025-10-06 11:55:00' },
    { id: 'wh-002', webhook: 'Legacy Billing System', event: 'invoice.paid', payloadSize: '1.2 KB', responseCode: 404, status: 'Failed', timestamp: '2025-10-06 11:50:00' },
    { id: 'wh-001', webhook: 'External Analytics Tool', event: 'order.completed', payloadSize: '4.0 KB', responseCode: 202, status: 'Pending', timestamp: '2025-10-06 11:45:00' },
];

// --- HELPER FUNCTIONS ---

const getStatusProps = (status, code) => {
    if (status === 'Success') return { color: 'success', icon: SuccessIcon };
    if (status === 'Pending') return { color: 'info', icon: PendingIcon };
    if (code >= 400 || status === 'Failed') return { color: 'error', icon: ErrorIcon };
    return { color: 'default', icon: PendingIcon };
};

// --- MAIN COMPONENT ---
const WebhookTests = () => {
    const [webhookLogs, setWebhookLogs] = useState(mockLogs);
    const [testTargetUrl, setTestTargetUrl] = useState(initialWebhooks[0].url);
    const [testEvent, setTestEvent] = useState('user.created');
    const [isTesting, setIsTesting] = useState(false);
    const theme = useTheme();

    const handleRunTest = () => {
        setIsTesting(true);
        // Simulate an API call delay
        const mockResponseCode = [200, 200, 403, 503][Math.floor(Math.random() * 4)];
        
        setTimeout(() => {
            const newLog = {
                id: `wh-${webhookLogs.length + 1}`,
                webhook: 'Manual Test',
                event: testEvent,
                payloadSize: '0.8 KB',
                responseCode: mockResponseCode,
                status: mockResponseCode === 200 ? 'Success' : (mockResponseCode >= 400 ? 'Failed' : 'Pending'),
                timestamp: new Date().toLocaleString(),
            };
            
            setWebhookLogs([newLog, ...webhookLogs]);
            setIsTesting(false);
        }, 1500); // 1.5 second test delay
    };

    const handleViewDetails = (logId) => {
        alert(`Showing full details for Webhook Log ID: ${logId}\n\nThis would typically open a modal with request headers, full payload, and server response body.`);
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom 
                sx={{ 
                    fontWeight: 800, 
                    color: theme.palette.primary.main, 
                    display: 'flex',
                    alignItems: 'center',
                    pb: 1
                }}
            >
                <ApiIcon sx={{ mr: 1.5, fontSize: '3rem', color: theme.palette.secondary.main }} />
                Webhook Test & Monitoring
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
                Track recent external system notifications and run diagnostic tests.
            </Typography>

            <Grid container spacing={4}>
                {/* --- Manual Test Card (High-Level Functionality) --- */}
                <Grid item xs={12} lg={4}>
                    <Card elevation={6} sx={{ height: '100%', p: 2, borderRadius: 2 }}>
                        <CardContent>
                            <Typography variant="h5" fontWeight={700} mb={2} display="flex" alignItems="center">
                                <SendIcon sx={{ mr: 1 }} color="secondary" /> Run Manual Test
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <TextField
                                fullWidth
                                select
                                label="Target Webhook"
                                value={testTargetUrl}
                                onChange={(e) => setTestTargetUrl(e.target.value)}
                                helperText="Select an active endpoint to test"
                                margin="normal"
                                SelectProps={{
                                    native: true,
                                }}
                            >
                                {initialWebhooks.map((option) => (
                                    <option key={option.url} value={option.url}>
                                        {option.name} - ({option.event})
                                    </option>
                                ))}
                            </TextField>

                            <TextField
                                fullWidth
                                label="Simulated Event Type"
                                value={testEvent}
                                onChange={(e) => setTestEvent(e.target.value)}
                                helperText="e.g., user.updated, order.cancelled"
                                margin="normal"
                            />

                            <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
                                Target URL: **{testTargetUrl}**
                            </Alert>

                            <Button
                                fullWidth
                                variant="contained"
                                color="secondary"
                                size="large"
                                startIcon={<SendIcon />}
                                onClick={handleRunTest}
                                disabled={isTesting}
                                sx={{ mt: 1 }}
                            >
                                {isTesting ? 'Testing...' : 'Dispatch Test Webhook'}
                            </Button>
                            
                            {isTesting && <LinearProgress color="secondary" sx={{ mt: 2 }} />}
                        </CardContent>
                    </Card>
                </Grid>

                {/* --- Logs Table (High-Level Monitoring) --- */}
                <Grid item xs={12} lg={8}>
                    <Paper elevation={6} sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h5" fontWeight={700} mb={2}>
                            Recent Webhook Activity Log
                        </Typography>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            The log shows **2 recent failures**. Check the details for response body errors.
                        </Alert>
                        <Table size="medium">
                            <TableHead sx={{ backgroundColor: theme.palette.grey[100] }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Webhook Target</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Event</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Response</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Timestamp</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Details</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {webhookLogs.map((log) => {
                                    const statusProps = getStatusProps(log.status, log.responseCode);
                                    return (
                                        <TableRow key={log.id} hover>
                                            <TableCell>{log.id}</TableCell>
                                            <TableCell>{log.webhook}</TableCell>
                                            <TableCell>{log.event}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={log.responseCode} 
                                                    color={statusProps.color} 
                                                    size="small"
                                                    sx={{ fontWeight: 'bold' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={log.status} 
                                                    color={statusProps.color} 
                                                    size="small"
                                                    icon={<statusProps.icon />}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption">{log.timestamp}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="View Request/Response Details">
                                                    <IconButton size="small" onClick={() => handleViewDetails(log.id)}>
                                                        <VisibilityIcon color="primary" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default WebhookTests;